import { isObjEmpty, isArray, clone } from '@helpers/utils.js'
import { computed, nextTick, ref, watch, readonly } from 'vue'
import { filterSettings, HEADERS } from '@config/game.js'
import isEqual from 'lodash/isEqual'
import {
  GAME_PLATFORM_LABELS,
  STATUS_NOT_ACCEPTED,
  STATUS_ACCEPTED,
  STATUS_DELETED,
  VISIBLE_HIDDEN,
  VISIBLE_PUBLIC,
} from '@config/options.js'

export const ADMIN_FILTER_OPTION = 'Admin Options'
export const LIBRARY_BROWSE_ID = 'library'
export const PLATFORM_OPTION = 'Platforms'

const PENDING_FILTER = { text: 'Show pending', value: 'pending' }
const DELETED_FILTER = { text: 'Show deleted', value: 'deleted' }
const HIDDEN_FILTER = { text: 'Show hidden', value: 'hidden' }

const ADMIN_FILTERS = [HIDDEN_FILTER, PENDING_FILTER, DELETED_FILTER]
const ADMIN_FILTERS_GUIDES = [PENDING_FILTER, DELETED_FILTER]

const state = ref({})
const loadingRef = ref({})
const filterMenuOpen = ref(true)

export default function (id) {
  if (!id) {
    throw new Error('useFilter requires id')
  }
  if (loadingRef.value[id] === undefined) {
    loadingRef.value[id] = true
  }

  const filter = computed(() => state.value[id] || [])

  const loading = computed(() => loadingRef.value[id])

  const activeFilters = computed(() =>
    _getSelected(filter.value).map(({ id, filter, tag, type }) => ({
      id,
      filter,
      tag,
      active: true,
      type,
    }))
  )

  const isFiltering = computed(() => _getSelected(filter.value).length > 0)

  const getFilter = computed(() => {
    return state.value[id] ? _getQueryObj() : null
  })

  const settingsTags = computed(() =>
    filterSettings.value.tags && id !== LIBRARY_BROWSE_ID
      ? filterSettings.value.tags.toLowerCase().split(',')
      : []
  )

  function updateFilter(option) {
    if (settingsTags.value.includes(option.value.toLowerCase())) {
      // Don't allow deselecting query param tag
      return
    }

    if (option.id === PLATFORM_OPTION) {
      const platform = Object.entries(GAME_PLATFORM_LABELS).find(
        ([, value]) => value === option.value
      )[0]

      HEADERS.platform = HEADERS.platform === platform ? null : platform
    }

    const updateTags = [...state.value[id]]
    const index = updateTags.findIndex(
      (tagOption) => tagOption.id === option.id
    )

    if (index !== -1) {
      const tag = { ...updateTags[index] }

      switch (option.type) {
        case 'dropdown':
          tag.selected = _handleDropboxes(tag, option)
          break
        case 'checkboxes':
          tag.selected = _handleCheckboxes(tag, option)
          break
      }

      updateTags.splice(index, 1, tag)
    }

    if (!isEqual(state.value[id], updateTags)) {
      state.value[id] = updateTags
    }
  }

  function resetFilter() {
    const newFilters = state.value?.[id]?.map((filter) => {
      const selected = []

      settingsTags.value.forEach((queryTag) => {
        const matchingTag = filter.tags.find(
          (tag) => tag.toLowerCase() === queryTag
        )
        if (matchingTag) {
          selected.push(matchingTag)
        }
      })

      return {
        ...filter,
        selected,
      }
    })

    if (newFilters.some((f) => f.id === PLATFORM_OPTION)) {
      HEADERS.platform = null
    }

    state.value[id] = newFilters || []
  }

  function setFilter(options) {
    nextTick(() => {
      // set loadingRef to false after the next dom update
      loadingRef.value[id] = false
      let result = [...options]

      result.forEach((option, index) => {
        const selected = state.value[id]?.find(
          (x) => x.id === option.id
        )?.selected

        if (selected && !result[index].selected.length) {
          result[index].selected = selected
        }
      })

      state.value[id] = isArray(result) ? [...result] : []
    })
  }

  function isFiltered() {
    return filter.value && filter.value.length !== 0
  }

  function inMemoryFilter(items) {
    let resultsCopy = items && clone(items)

    if (activeFilters.value.length > 0) {
      activeFilters.value
        .filter(
          (f) => ![ADMIN_FILTER_OPTION, PLATFORM_OPTION].includes(f.filter)
        )
        .forEach(({ tag }) => {
          resultsCopy = resultsCopy.filter(
            (item) =>
              !item.tags ||
              item.tags.find(
                ({ name }) => name.toLowerCase() === tag.toLowerCase()
              )
          )
        })
    }

    return resultsCopy ? resultsCopy : []
  }

  function watchFilters(callback, immediate = true) {
    watch(
      filter,
      (value) => {
        callback(value)
      },
      { immediate }
    )
  }

  function _handleDropboxes(tag, options) {
    return tag.selected.includes(options.value) ? [] : [options.value]
  }

  function _handleCheckboxes(tag, options) {
    return tag.selected.includes(options.value)
      ? tag.selected.filter((item) => item !== options.value)
      : [...tag.selected, options.value]
  }

  function _getSelected(filters) {
    const selected = []

    if (filters) {
      filters
        .filter((filter) => filter.selected.length !== 0)
        .forEach((filtered) => {
          filtered.selected.forEach((tagName) => {
            selected.push({
              id: filtered.id,
              filter: filtered.name,
              tag: tagName,
              type: filtered.type,
            })
          })
        })
    }

    return selected
  }

  function _getQueryObj() {
    const selected = _getSelected(filter.value)
    if (selected.length === 0) {
      return {}
    }

    const queryObj = {}
    queryObj['tags-in'] = selected
      .filter((_selected) => _selected.filter !== ADMIN_FILTER_OPTION)
      .filter((_selected) => _selected.filter !== PLATFORM_OPTION)
      .map((_selected) => _selected.tag)
      .join(',')

    queryObj['admin'] = selected
      .filter((_selected) => _selected.filter === ADMIN_FILTER_OPTION)
      .map(
        (_selected) =>
          ADMIN_FILTERS.find((_filter) => _filter.text === _selected.tag).value
      )
      .join(',')

    queryObj['platforms'] = selected
      .filter((_selected) => _selected.filter === PLATFORM_OPTION)
      .map(
        (_selected) =>
          Object.entries(GAME_PLATFORM_LABELS).find(
            ([, value]) => value === _selected.tag
          )[0]
      )
      .join(',')

    if (!queryObj['admin']) delete queryObj['admin']

    return queryObj
  }

  function toggleFilterMenu() {
    filterMenuOpen.value = !filterMenuOpen.value
  }

  return {
    filterMenuOpen: readonly(filterMenuOpen),
    toggleFilterMenu,
    inMemoryFilter,
    activeFilters,
    settingsTags,
    updateFilter,
    watchFilters,
    resetFilter,
    isFiltering,
    isFiltered,
    setFilter,
    getFilter,
    loading,
    filter,
  }
}

export function normalizeModFilters(options, filterQuery, useAdminFilters) {
  const filters = [...options]

  if (useAdminFilters) {
    filters.unshift({
      id: ADMIN_FILTER_OPTION.replace(' ', '-'),
      hideInTagsForm: true,
      name: ADMIN_FILTER_OPTION,
      selected: [],
      tags: ADMIN_FILTERS.map((f) => f.text),
      type: 'checkboxes',
    })
  }

  return filterQuery && !isObjEmpty(filterQuery)
    ? _mergeFilterQuery(filters, filterQuery)
    : filters
}

export function normalizeGuideFilters(options, filterQuery, useAdminFilters) {
  const filters = [..._getGuideFilterOptions(options)]

  if (useAdminFilters) {
    filters.unshift({
      id: ADMIN_FILTER_OPTION.replace(' ', '-'),
      hideInTagsForm: true,
      statusOption: true,
      name: ADMIN_FILTER_OPTION,
      selected: [],
      tags: ADMIN_FILTERS_GUIDES.map((f) => f.text),
      type: 'checkboxes',
    })
  }

  return filterQuery && !isObjEmpty(filterQuery)
    ? _mergeFilterQuery(filters, filterQuery)
    : filters
}

function _getGuideFilterOptions(filters) {
  const tags = filters.map((filter) => filter.name).filter((n) => n.length)

  return [
    {
      id: 'T-0',
      hidden: false,
      name: 'Filter',
      selected: [],
      tags,
      type: 'checkboxes',
      tag_count_map: Object.fromEntries(
        filters.map((filter) => [filter.name, filter.count])
      ),
    },
  ]
}

function _mergeFilterQuery(options, filterQuery) {
  const filters = clone(options)

  Object.entries(filterQuery).forEach(([key, queryString]) => {
    const tags = queryString.split(',').map((t) => t.toLowerCase())

    switch (key) {
      case 'tags-in':
        filters.forEach((f) => {
          if (f.id.startsWith('T-')) {
            f.tags.forEach((optionTag) => {
              if (tags.includes(optionTag.toLowerCase())) {
                f.selected.push(optionTag)
              }
            })
          }
        })
        break

      case 'admin': {
        const filter = filters.find((f) => f.name === ADMIN_FILTER_OPTION)
        if (filter) {
          filter.selected.push(
            ...ADMIN_FILTERS.filter((a) => tags.includes(a.value)).map(
              (a) => a.text
            )
          )
        }
        break
      }
    }
  })

  return filters
}

export function getAPIFilter(query) {
  if (!query.admin) return query

  query['status-in'] = [STATUS_ACCEPTED]

  query.admin.split(',').forEach((q) => {
    switch (q) {
      case HIDDEN_FILTER.value:
        query['visible-in'] = `${VISIBLE_HIDDEN},${VISIBLE_PUBLIC}`
        break

      case PENDING_FILTER.value:
        query['status-in'].unshift(STATUS_NOT_ACCEPTED)
        break

      case DELETED_FILTER.value:
        query['status-in'].push(STATUS_DELETED)
        break
    }
  })

  if (query['status-in'].length > 1) {
    query['status-in'] = query['status-in'].join(',')
  } else {
    delete query['status-in']
  }
  delete query.admin
  return query
}
